import { useEffect, useRef, useState } from "react";
import {
  formDisplayName,
  MEDICAL_EVIDENCE_TEMPLATE_KEY,
  type EvidenceSummary,
  type EvidenceSummaryUpload,
  type MeasurementReviewStatus,
  type PatientGroup,
} from "@care-by-ella/contracts";
import {
  assessEarlierLabUpload,
  fetchEvidenceSummary,
  fetchPatientUploadBlob,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  assessedByText,
  dateSv,
  evidenceBlocks,
  evidenceStatusRows,
  type EvidenceStatusRow,
  type TransferReviewState,
} from "@/lib/evidence-status";
import { MeasurementReviewControl } from "./MeasurementReview";
import { StatusTag } from "./StatusTag";
import { useReadOnly } from "./SupportView";

// TES-949 (TES-941 §INSKICKAT MEDICINSKT UNDERLAG) — the compact card next to
// "Riskflaggor: X", on the overview and in the meeting: which evidence the
// patient has actually handed over across her whole journey. F-07 is the
// primary source for a prescription transfer; CP-06 uploads count too.
//
// Facts only. Emelie + Kirill:
// "Vi ska inte visa ”nytt blodprov: svar finns” eller liknande automatiska statusar eftersom vi ännu inte har integration mot Webdoc."
// So "Uppladdat" means a file was received, nothing more, and the age of a
// lab result is a number with no colour and no threshold.

// Where a file came from: F-07's readable name, or the komplettering (CP-06).
function sourceName(templateKey: string): string {
  return templateKey === MEDICAL_EVIDENCE_TEMPLATE_KEY
    ? "Komplettering"
    : formDisplayName(templateKey);
}

function Row({
  label,
  subordinate,
  children,
}: {
  label: string;
  subordinate: boolean;
  children: React.ReactNode;
}) {
  // TES-1037 — a subordinate row follows from the one above it (no lab result
  // uploaded, so its date and age cannot be assessed): indented and quieter.
  return (
    <div
      className={`flex flex-col gap-[2px] ${
        subordinate ? "border-l-2 border-surfaceMuted pl-sm" : ""
      }`}
    >
      <dt
        className={`text-xs ${subordinate ? "text-textTertiary" : "text-textSecondary"}`}
      >
        {label}
      </dt>
      <dd className="text-sm text-textPrimary">{children}</dd>
    </div>
  );
}

export function EvidenceSummaryCard({
  patientId,
  onUnauthorized,
  className = "",
  embedded = false,
  onReviewChanged,
  patientGroup,
  transferReview,
  bare = false,
}: {
  patientId: string;
  onUnauthorized: () => void;
  className?: string;
  // TES-1329 — read the facts by her journey: "Frivilligt underlag" for a
  // new-treatment patient, "Obligatoriskt underlag för receptövertag" (plus
  // her voluntary items) for a transfer (lib/evidence-status
  // `evidenceBlocks`). Absent: the journey-blind rows, as before.
  patientGroup?: PatientGroup;
  // The transfer review, for the required document's status.
  transferReview?: TransferReviewState | null;
  // No frame and no heading of its own: the caller's heading sits above.
  bare?: boolean;
  // TES-1037 — inside the transfer review's F-07 column: the same rows and
  // tags, under a column sub-heading and without the file lists, because the
  // column already lists her attachments right above.
  embedded?: boolean;
  // TES-1056 — told when the doctor changes his assessment of the reading
  // here, so a list of the same readings beside the card can follow.
  onReviewChanged?: (
    sessionId: string,
    status: MeasurementReviewStatus,
  ) => void;
}) {
  const [data, setData] = useState<EvidenceSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [openUrl, setOpenUrl] = useState<string | null>(null);
  const [assessing, setAssessing] = useState<string | null>(null);
  // TES-1040 — the admin's read-only view shows the mark, never the button.
  const readOnly = useReadOnly();
  // Read through a ref so a parent re-render does not refetch: every fetch
  // here is a logged patient read.
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let cancelled = false;
    fetchEvidenceSummary(patientId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
        else setError("Kunde inte hämta underlaget.");
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  // The previous object URL is released when a new one replaces it and on
  // unmount; the file only ever lives in memory (TES-934).
  useEffect(() => {
    return () => {
      if (openUrl) URL.revokeObjectURL(openUrl);
    };
  }, [openUrl]);

  async function view(u: EvidenceSummaryUpload) {
    if (openId === u.id) {
      setOpenId(null);
      setOpenUrl(null);
      return;
    }
    try {
      const blob = await fetchPatientUploadBlob(patientId, u.id);
      setOpenUrl(URL.createObjectURL(blob));
      setOpenId(u.id);
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
      else setError("Kunde inte öppna filen.");
    }
  }

  // TES-1125 — "Tidigare provsvar är bedömt av läkare": the doctor marks an
  // earlier lab file assessed once he has looked at it. Uploaded is not
  // reviewed, so the mark is his, per file, with his name and the date.
  async function assess(u: EvidenceSummaryUpload) {
    setAssessing(u.id);
    setError(null);
    try {
      setData(await assessEarlierLabUpload(patientId, u.id));
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
      else setError("Kunde inte spara bedömningen.");
    } finally {
      setAssessing(null);
    }
  }

  const assessment = (u: EvidenceSummaryUpload) => {
    // Only earlier lab files carry the field at all.
    if (u.assessedAt === undefined) return null;
    if (u.assessedAt) {
      return (
        <span className="block text-xs text-textSecondary">
          {assessedByText(u.assessedByName, u.assessedAt)}
        </span>
      );
    }
    if (readOnly) return null;
    return (
      <button
        type="button"
        onClick={() => void assess(u)}
        disabled={assessing !== null}
        className="block text-xs text-brand underline disabled:opacity-50"
      >
        Markera som bedömd
      </button>
    );
  };

  const files = (uploads: EvidenceSummaryUpload[]) => {
    if (embedded || uploads.length === 0) return null;
    return (
      <ul className="mt-xs flex flex-col gap-xs">
        {uploads.map((u) => (
          <li key={u.id}>
            <button
              type="button"
              className="break-all text-left text-brand underline"
              onClick={() => void view(u)}
            >
              {u.filename}
            </button>
            <span className="text-xs text-textSecondary">
              {" "}
              · {sourceName(u.templateKey)} {dateSv(u.createdAt)}
            </span>
            {assessment(u)}
            {openId === u.id && openUrl ? (
              <div className="mt-xs">
                {u.mimeType === "application/pdf" ? (
                  <>
                    <iframe
                      src={openUrl}
                      title={u.filename}
                      className="h-[480px] w-full rounded-md border border-surfaceMuted"
                    />
                    <a
                      href={openUrl}
                      download={u.filename}
                      className="text-xs text-brand underline"
                    >
                      Ladda ner {u.filename}
                    </a>
                  </>
                ) : (
                  <img
                    src={openUrl}
                    alt={u.filename}
                    className="max-h-[420px] rounded-md border border-surfaceMuted"
                  />
                )}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    );
  };

  const rowList = (d: EvidenceSummary, rows: EvidenceStatusRow[]) => (
    <dl className="flex flex-col gap-sm">
      {rows.map((row) => (
        <Row key={row.key} label={row.label} subordinate={row.subordinate}>
          <span className="flex flex-wrap gap-xs">
            {row.tags.map((t) => {
              const bp = d.bloodPressurePulse;
              // TES-1056 — her reading's assessment: the doctor sets it
              // here; in the admin's read-only view it stays a tag.
              if (t.review && bp?.sessionId) {
                const sessionId = bp.sessionId;
                return (
                  <MeasurementReviewControl
                    key={t.text}
                    patientId={patientId}
                    sessionId={sessionId}
                    status={bp.reviewStatus ?? "patient_reported"}
                    onUnauthorized={onUnauthorizedRef.current}
                    onChanged={(status) => {
                      setData((prev) =>
                        prev && prev.bloodPressurePulse
                          ? {
                              ...prev,
                              bloodPressurePulse: {
                                ...prev.bloodPressurePulse,
                                reviewStatus: status,
                              },
                            }
                          : prev,
                      );
                      onReviewChanged?.(sessionId, status);
                    }}
                  />
                );
              }
              return (
                <StatusTag
                  key={t.text}
                  tone={t.tone}
                  subordinate={row.subordinate}
                >
                  {t.text}
                </StatusTag>
              );
            })}
          </span>
          {row.detail ? (
            <span className="mt-[2px] block text-xs text-textSecondary">
              {row.detail}
            </span>
          ) : null}
          {row.key === "prescription"
            ? files(d.prescriptionDocumentation.uploads)
            : row.key === "priorLabs"
              ? files(d.priorLabResults.uploads)
              : null}
        </Row>
      ))}
    </dl>
  );

  const body = !data ? (
    error ? null : (
      <p className="text-sm text-textSecondary">Hämtar…</p>
    )
  ) : patientGroup ? (
    <div className="flex flex-col gap-md">
      {evidenceBlocks(data, patientGroup, transferReview).map((b) => (
        <section key={b.key} data-testid={`evidence-block-${b.key}`}>
          <h4 className="mb-xs text-xs font-semibold uppercase tracking-wide text-textSecondary">
            {b.heading}
          </h4>
          {b.rows.length > 0 ? (
            rowList(data, b.rows)
          ) : (
            <p className="text-sm text-textSecondary">{b.emptyText}</p>
          )}
        </section>
      ))}
    </div>
  ) : (
    rowList(data, evidenceStatusRows(data))
  );

  if (bare) {
    return (
      <div className={className}>
        {error ? <p className="mb-sm text-sm text-danger">{error}</p> : null}
        {body}
      </div>
    );
  }

  return (
    <section
      className={
        embedded
          ? className
          : `rounded-md border border-surfaceMuted bg-surface p-md ${className}`
      }
    >
      {embedded ? (
        <h3 className="mb-xs text-xs font-semibold uppercase tracking-wide text-textSecondary">
          Inskickat medicinskt underlag
        </h3>
      ) : (
        <h2 className="mb-sm font-display text-lg">
          Inskickat medicinskt underlag
        </h2>
      )}
      {error ? <p className="mb-sm text-sm text-danger">{error}</p> : null}
      {body}
    </section>
  );
}
